import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';

const MIN_CELL = 20;
const DEFAULT_HEADER = 32;

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const ensureArray = (arr: unknown, count: number, fallbackTotal: number): number[] => {
  if (Array.isArray(arr) && arr.length === count && arr.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) {
    return arr as number[];
  }
  const even = Math.max(MIN_CELL, fallbackTotal / Math.max(1, count));
  return Array(count).fill(even);
};

const sum = (arr: number[]) => arr.reduce((s, n) => s + n, 0);

export interface ScaleTableCommandParams {
  nodeId: string;
  scaleX: number;
  scaleY: number;
  position: { x: number; y: number };
}

export class ScaleTableCommand extends BaseNodeCommand {
  constructor(private params: ScaleTableCommandParams) {
    super('Scale Table');
  }

  protected doExecute(): NodeChangeRecord[] {
    const mutationManager = this.getMutationManager() as any;
    const node = mutationManager.getNode(this.params.nodeId) as any;
    if (!node) return [];
    if (node.type !== 'table') return [];

    const attrs = (node.attrs ?? {}) as Record<string, unknown>;

    const rowCount = clampCount(attrs.rowCount, 3);
    const colCount = clampCount(attrs.columnCount, 3);
    const showHeader = attrs.showHeader !== false;

    const width = (node.width ?? node.w ?? 240) as number;
    const height = (node.height ?? node.h ?? 180) as number;

    const headerHeight = typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && (attrs.headerHeight as number) > 0
      ? Math.max(MIN_CELL, attrs.headerHeight as number)
      : DEFAULT_HEADER;

    const bodyHeight = Math.max(MIN_CELL * rowCount, height - (showHeader ? headerHeight : 0));
    const rowHeights = ensureArray(attrs.rowHeights, rowCount, bodyHeight);
    const colWidths = ensureArray(attrs.columnWidths, colCount, width);

    const safeScaleX = Number.isFinite(this.params.scaleX) && this.params.scaleX > 0 ? this.params.scaleX : 1;
    const safeScaleY = Number.isFinite(this.params.scaleY) && this.params.scaleY > 0 ? this.params.scaleY : 1;

    const nextColWidths = colWidths.map((w) => Math.max(MIN_CELL, w * safeScaleX));
    const nextRowHeights = rowHeights.map((h) => Math.max(MIN_CELL, h * safeScaleY));
    const nextHeaderHeight = showHeader ? Math.max(MIN_CELL, headerHeight * safeScaleY) : 0;

    const nextWidth = sum(nextColWidths);
    const nextBodyHeight = sum(nextRowHeights);
    const nextHeight = nextBodyHeight + (showHeader ? nextHeaderHeight : 0);

    const changes = {
      x: this.params.position.x,
      y: this.params.position.y,
      width: nextWidth,
      w: nextWidth,
      height: nextHeight,
      h: nextHeight,
      attrs: {
        columnWidths: nextColWidths,
        rowHeights: nextRowHeights,
        headerHeight: nextHeaderHeight,
      },
    };

    const previewAttrs = { ...(attrs as any), ...(changes.attrs as any) };
    const preview: any = { ...node, ...changes, attrs: previewAttrs };
    const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, useDiagramStore.getState().nodes);

    if (dockedUpdates.length > 0 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      return mutationManager.applyBatchNodeChanges([{ nodeId: this.params.nodeId, changes }, ...dockedUpdates]) ?? [];
    }
    const rec = mutationManager.applyNodeChange(this.params.nodeId, changes);
    return rec ? [rec] : [];
  }
}

